import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import {
  CandlestickController,
  CandlestickElement,
} from 'chartjs-chart-financial';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-luxon';

import { fetchOHLC } from '../utils/coingecko';

ChartJS.register(
  TimeScale,
  LinearScale,
  Tooltip,
  Legend,
  CandlestickController,
  CandlestickElement,
  zoomPlugin
);

export default function BitcoinChart() {
  const [ohlcData, setOhlcData] = useState([]);

  // Load initial candles (15 for mobile)
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchOHLC('bitcoin', 'usd', 1);
        const last15 = data.slice(-15);
        setOhlcData(last15);
      } catch (err) {
        console.error('Failed to fetch OHLC data:', err);
      }
    };
    loadData();
  }, []);

  // Live update every 20s
  useEffect(() => {
    const update = async () => {
      try {
        const res = await fetch(
          'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd'
        );
        const data = await res.json();
        const price = data.bitcoin.usd;
        const now = Date.now();
        const nowMinute = Math.floor(now / 60000);

        setOhlcData(prev => {
          if (prev.length === 0) return prev;

          const last = prev[prev.length - 1];
          const lastMinute = Math.floor(last.time / 60000);
          let updated = [...prev];

          if (nowMinute === lastMinute) {
            const updatedCandle = {
              ...last,
              high: Math.max(last.high, price),
              low: Math.min(last.low, price),
              close: price,
            };
            updated[updated.length - 1] = updatedCandle;
          } else {
            const newCandle = {
              time: now,
              open: last.close,
              high: price,
              low: price,
              close: price,
            };
            updated.push(newCandle);
          }

          if (updated.length > 15) {
            updated = updated.slice(updated.length - 15);
          }

          return updated;
        });
      } catch (err) {
        console.error('Failed to update price:', err);
      }
    };

    update();
    const interval = setInterval(update, 20000);
    return () => clearInterval(interval);
  }, []);

  const chartData = {
    datasets: [
      {
        label: 'BTC/USD',
        data: ohlcData.map(candle => ({
          x: candle.time,
          o: candle.open,
          h: candle.high,
          l: candle.low,
          c: candle.close,
        })),
        color: {
          up: '#00ff00',
          down: '#ff0000',
          unchanged: '#999999',
        },
        // 🧱 Make candles thicker (reduce spacing)
        barThickness: 13,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'index',
        intersect: false,
        titleFont: { size: 10 },
        bodyFont: { size: 10 },
      },
      zoom: {
        pan: {
          enabled: false,
          mode: 'x',
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: 'x',
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'minute',
          tooltipFormat: 'HH:mm',
        },
        ticks: {
          source: 'auto',
          font: {
            size: 10, // ⬅️ Smaller font for time labels
          },
        },
      },
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: 'Price (USD)',
          font: {
            size: 10, // ⬅️ Smaller font for axis title
          },
        },
        ticks: {
          font: {
            size: 10, // ⬅️ Smaller font for price labels
          },
        },
      },
    },
  };

  return (
    <div className='candlestick_container'>
      <Chart type="candlestick" data={chartData} options={chartOptions} />
    </div>
  );
}
